import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import {
  EmployeeClothingEntitlement,
  EmployeeClothingEntitlementsSource,
  ClothingLossCase,
  ClothingRequest,
  EmployeeClothingOverview,
  EmployeeClothingPosition
} from '../models/clothing.model';
import { ProfileClothingViewItem } from '../models/profile-clothing.model';

interface ClothingStorekeeperActionSource {
  hasActiveRequest?: boolean;
  activeRequestId?: string;
  activeRequestStatus?: string;
  directIssueAllowed?: boolean;
  issueByRequestAllowed?: boolean;
  rejectNoStockAllowed?: boolean;
}

var CLOTHING_REASON_REQUEST_EXISTS = 'CLOTHING_ACTION_REASON_REQUEST_EXISTS';
var CLOTHING_REASON_REQUEST_NOT_ALLOWED = 'CLOTHING_ACTION_REASON_REQUEST_NOT_ALLOWED';
var CLOTHING_REASON_LOSS_PENDING = 'CLOTHING_ACTION_REASON_LOSS_PENDING';
var CLOTHING_REASON_NO_ISSUED_ITEM = 'CLOTHING_ACTION_REASON_NO_ISSUED_ITEM';
var CLOTHING_REASON_REQUEST_IN_PROGRESS = 'CLOTHING_ACTION_REASON_REQUEST_IN_PROGRESS';
var CLOTHING_REASON_ISSUE_NOT_ALLOWED = 'CLOTHING_ACTION_REASON_ISSUE_NOT_ALLOWED';
var CLOTHING_REASON_ALREADY_ISSUED = 'CLOTHING_ACTION_REASON_ALREADY_ISSUED';
var CLOTHING_REASON_NO_AVAILABLE_QUANTITY = 'CLOTHING_ACTION_REASON_NO_AVAILABLE_QUANTITY';
var CLOTHING_REASON_EXTEND_NOT_ALLOWED = 'CLOTHING_ACTION_REASON_EXTEND_NOT_ALLOWED';
var CLOTHING_REASON_REJECT_NOT_ALLOWED = 'CLOTHING_ACTION_REASON_REJECT_NOT_ALLOWED';
var CLOTHING_REASON_TEMPORARY_COVERAGE = 'CLOTHING_ACTION_REASON_TEMPORARY_COVERAGE';
var POSITION_STATUS_RESET_AFTER_LOSS = 'RESET_AFTER_LOSS';

@Injectable()
export class ProfileClothingViewService {
  constructor(private translate: TranslateService) {}

  mapEmployeeOverview(
    overview: EmployeeClothingOverview,
    entitlementsSource?: EmployeeClothingEntitlementsSource
  ): ProfileClothingViewItem[] {
    return this.createEmployeeItems(overview, entitlementsSource);
  }

  mapStorekeeperOverview(
    overview: EmployeeClothingOverview,
    entitlements: EmployeeClothingEntitlementsSource
  ): ProfileClothingViewItem[] {
    return this.createStorekeeperItems(overview, entitlements);
  }

  mapSelfStorekeeperOverview(
    overview: EmployeeClothingOverview,
    entitlements: EmployeeClothingEntitlementsSource
  ): ProfileClothingViewItem[] {
    return this.createSelfStorekeeperItems(overview, entitlements);
  }

  mapHeadPositions(positions: EmployeeClothingPosition[]): ProfileClothingViewItem[] {
    if (!positions || !positions.length) {
      return [];
    }
    return positions.map((position) => this.createHeadItem(position));
  }

  private createItems(
    overview: EmployeeClothingOverview,
    entitlementsSource: EmployeeClothingEntitlementsSource
  ): ProfileClothingViewItem[] {
    var positions = overview && overview.positions ? overview.positions : [];
    var entitlements = this.resolveEntitlements(entitlementsSource);
    var requestLookup = this.createRequestLookup(overview && overview.activeRequests);
    var lossLookup = this.createLossLookup(overview && overview.openLossCases);
    var positionLookup = this.createPositionLookup(positions);
    var entitlementLookup = this.createEntitlementLookup(entitlements);

    return this.createCatalogItemIds(entitlements, positions).map((catalogItemId) => {
      return this.createItem(
        positionLookup[catalogItemId],
        entitlementLookup[catalogItemId],
        requestLookup[catalogItemId],
        !!lossLookup[catalogItemId]
      );
    });
  }

  private createEmployeeItems(
    overview: EmployeeClothingOverview,
    entitlementsSource?: EmployeeClothingEntitlementsSource
  ): ProfileClothingViewItem[] {
    var positions = overview && overview.positions ? overview.positions : [];
    var entitlements = this.resolveEntitlements(entitlementsSource);
    var requestLookup = this.createRequestLookup(overview && overview.activeRequests);
    var lossLookup = this.createLossLookup(overview && overview.openLossCases);
    var positionLookup = this.createPositionLookup(positions);
    var entitlementLookup = this.createEntitlementLookup(entitlements);

    return this.createCatalogItemIds(entitlements, positions).map((catalogItemId) => {
      return this.createItem(
        positionLookup[catalogItemId],
        entitlementLookup[catalogItemId],
        requestLookup[catalogItemId],
        !!lossLookup[catalogItemId]
      );
    });
  }

  private createStorekeeperItems(
    overview: EmployeeClothingOverview,
    entitlementsSource: EmployeeClothingEntitlementsSource
  ): ProfileClothingViewItem[] {
    var positions = overview && overview.positions ? overview.positions : [];
    var entitlements = this.resolveEntitlements(entitlementsSource);
    var requestLookup = this.createRequestLookup(overview && overview.activeRequests);
    var lossLookup = this.createLossLookup(overview && overview.openLossCases);
    var positionLookup = this.createPositionLookup(positions);
    var entitlementLookup = this.createEntitlementLookup(entitlements);

    return this.createCatalogItemIds(entitlements, positions).map((catalogItemId) => {
      return this.createStorekeeperItem(
        positionLookup[catalogItemId],
        entitlementLookup[catalogItemId],
        requestLookup[catalogItemId],
        !!lossLookup[catalogItemId]
      );
    });
  }

  private createSelfStorekeeperItems(
    overview: EmployeeClothingOverview,
    entitlementsSource: EmployeeClothingEntitlementsSource
  ): ProfileClothingViewItem[] {
    var positions = overview && overview.positions ? overview.positions : [];
    var entitlements = this.resolveEntitlements(entitlementsSource);
    var requestLookup = this.createRequestLookup(overview && overview.activeRequests);
    var lossLookup = this.createLossLookup(overview && overview.openLossCases);
    var positionLookup = this.createPositionLookup(positions);
    var entitlementLookup = this.createEntitlementLookup(entitlements);

    return this.createCatalogItemIds(entitlements, positions).map((catalogItemId) => {
      return this.createSelfStorekeeperItem(
        positionLookup[catalogItemId],
        entitlementLookup[catalogItemId],
        requestLookup[catalogItemId],
        !!lossLookup[catalogItemId]
      );
    });
  }

  private createRequestLookup(requests: ClothingRequest[]): { [key: string]: ClothingRequest } {
    var lookup: { [key: string]: ClothingRequest } = {};
    if (!requests || !requests.length) {
      return lookup;
    }
    requests.forEach((request) => {
      lookup[request.catalogItemId] = request;
    });
    return lookup;
  }

  private resolveEntitlements(
    entitlementsSource: EmployeeClothingEntitlementsSource
  ): EmployeeClothingEntitlement[] {
    if (!entitlementsSource) {
      return [];
    }
    if (Array.isArray(entitlementsSource)) {
      return entitlementsSource;
    }
    if (!entitlementsSource.items || !entitlementsSource.items.length) {
      return [];
    }
    return entitlementsSource.items;
  }

  private createLossLookup(lossCases: ClothingLossCase[]): { [key: string]: boolean } {
    var lookup: { [key: string]: boolean } = {};
    if (!lossCases || !lossCases.length) {
      return lookup;
    }
    lossCases.forEach((lossCase) => {
      lookup[lossCase.catalogItemId] = true;
    });
    return lookup;
  }

  private createPositionLookup(
    positions: EmployeeClothingPosition[]
  ): { [key: string]: EmployeeClothingPosition } {
    var lookup: { [key: string]: EmployeeClothingPosition } = {};
    if (!positions || !positions.length) {
      return lookup;
    }
    positions.forEach((position) => {
      lookup[position.catalogItemId] = position;
    });
    return lookup;
  }

  private createEntitlementLookup(
    entitlements: EmployeeClothingEntitlement[]
  ): { [key: string]: EmployeeClothingEntitlement } {
    var lookup: { [key: string]: EmployeeClothingEntitlement } = {};
    if (!entitlements || !entitlements.length) {
      return lookup;
    }
    entitlements.forEach((entitlement) => {
      lookup[entitlement.catalogItemId] = entitlement;
    });
    return lookup;
  }

  private createCatalogItemIds(
    entitlements: EmployeeClothingEntitlement[],
    positions: EmployeeClothingPosition[]
  ): string[] {
    var catalogItemIds: string[] = [];
    this.appendEntitlementIds(catalogItemIds, entitlements);
    this.appendPositionIds(catalogItemIds, positions);
    return catalogItemIds;
  }

  private appendEntitlementIds(
    catalogItemIds: string[],
    entitlements: EmployeeClothingEntitlement[]
  ): void {
    if (!entitlements || !entitlements.length) {
      return;
    }
    entitlements.forEach((entitlement) => {
      this.appendCatalogItemId(catalogItemIds, entitlement.catalogItemId);
    });
  }

  private appendPositionIds(
    catalogItemIds: string[],
    positions: EmployeeClothingPosition[]
  ): void {
    if (!positions || !positions.length) {
      return;
    }
    positions.forEach((position) => {
      this.appendCatalogItemId(catalogItemIds, position.catalogItemId);
    });
  }

  private appendCatalogItemId(catalogItemIds: string[], catalogItemId: string): void {
    if (catalogItemIds.indexOf(catalogItemId) !== -1) {
      return;
    }
    catalogItemIds.push(catalogItemId);
  }

  private createItem(
    position: EmployeeClothingPosition,
    entitlement: EmployeeClothingEntitlement,
    request: ClothingRequest,
    hasOpenLossCase: boolean
  ): ProfileClothingViewItem {
    var hasActiveRequest = this.hasActiveRequest(position, entitlement, request);
    var requestId = this.getActiveRequestId(position, entitlement, request);
    var canIssue = this.canIssue(position, entitlement, request, hasOpenLossCase);
    var lifePercent = this.calculateLifePercent(position, entitlement);
    var hasIssuedPosition = !!position;
    var rejectDisabled = this.isRejectDisabled(position, entitlement, request);
    var temporaryCoverage = this.hasTemporaryNoStockCoverage(position, entitlement);

    return {
      positionId: position ? position.positionId : '',
      catalogItemId: this.getCatalogItemId(position, entitlement),
      requestId: requestId,
      name: this.getCatalogItemName(position, entitlement),
      seasonKey: this.createSeasonKey(this.getSeason(position, entitlement)),
      quantity: this.getQuantity(position, entitlement),
      maxQuantity: this.getMaxQuantity(position, entitlement, request),
      issueDate: this.getIssueDate(position),
      serviceLifeText: this.createServiceLifeText(position, entitlement),
      lifePercent: lifePercent,
      lifePercentText: this.createLifePercentText(lifePercent),
      lifeBarClass: this.createLifeBarClass(position, entitlement, lifePercent, hasIssuedPosition),
      temporarilyCoveredByNoStock: temporaryCoverage,
      temporaryNoStockCoverageUntil: this.getTemporaryNoStockCoverageUntil(position, entitlement),
      temporaryCoverageOnly: this.isTemporaryCoverageOnly(position, entitlement),
      canIssue: canIssue,
      issueByRequest: false,
      requestDisabled: this.isRequestDisabled(position, entitlement, request, hasOpenLossCase),
      requestDisabledReasonKey: this.getRequestDisabledReasonKey(
        position,
        entitlement,
        request,
        hasOpenLossCase
      ),
      lossDisabled: this.isLossDisabled(position, request, hasOpenLossCase),
      lossDisabledReasonKey: this.getLossDisabledReasonKey(position, request, hasOpenLossCase),
      issueDisabled: !canIssue,
      issueDisabledReasonKey: this.getIssueDisabledReasonKey(
        position,
        entitlement,
        request,
        hasOpenLossCase
      ),
      rejectDisabled: rejectDisabled,
      rejectDisabledReasonKey: this.getRejectDisabledReasonKey(position, entitlement, request),
      hasActiveRequest: hasActiveRequest,
      hasIssuedPosition: hasIssuedPosition,
      isEntitlementOnly: !hasIssuedPosition,
      canHeadExtend: false,
      headExtendDisabled: true,
      headExtendDisabledReasonKey: CLOTHING_REASON_EXTEND_NOT_ALLOWED,
      issueQuantity: this.getIssueQuantity(position, entitlement, request),
      requestedQuantity: request ? request.requestedQuantity : 0
    };
  }

  private createHeadItem(position: EmployeeClothingPosition): ProfileClothingViewItem {
    var lifePercent = this.calculateLifePercent(position, null);

    return {
      positionId: position.positionId,
      catalogItemId: position.catalogItemId,
      requestId: '',
      name: position.catalogItemName,
      seasonKey: this.createSeasonKey(position.season),
      quantity: position.activeQuantity,
      maxQuantity: this.getHeadMaxQuantity(position),
      issueDate: this.getIssueDate(position),
      serviceLifeText: this.createServiceLifeText(position, null),
      lifePercent: lifePercent,
      lifePercentText: this.createLifePercentText(lifePercent),
      lifeBarClass: this.createLifeBarClass(position, null, lifePercent, true),
      temporarilyCoveredByNoStock: this.hasTemporaryNoStockCoverage(position, null),
      temporaryNoStockCoverageUntil: this.getTemporaryNoStockCoverageUntil(position, null),
      temporaryCoverageOnly: false,
      canIssue: false,
      issueByRequest: false,
      requestDisabled: true,
      requestDisabledReasonKey: CLOTHING_REASON_REQUEST_NOT_ALLOWED,
      lossDisabled: true,
      lossDisabledReasonKey: CLOTHING_REASON_NO_ISSUED_ITEM,
      issueDisabled: true,
      issueDisabledReasonKey: CLOTHING_REASON_ISSUE_NOT_ALLOWED,
      rejectDisabled: true,
      rejectDisabledReasonKey: '',
      hasActiveRequest: false,
      hasIssuedPosition: true,
      isEntitlementOnly: false,
      canHeadExtend: this.canHeadExtend(position),
      headExtendDisabled: !this.canHeadExtend(position),
      headExtendDisabledReasonKey: CLOTHING_REASON_EXTEND_NOT_ALLOWED,
      issueQuantity: 0,
      requestedQuantity: 0
    };
  }

  private createSelfStorekeeperItem(
    position: EmployeeClothingPosition,
    entitlement: EmployeeClothingEntitlement,
    request: ClothingRequest,
    hasOpenLossCase: boolean
  ): ProfileClothingViewItem {
    var item = this.createItem(position, entitlement, request, hasOpenLossCase);
    var canIssue = this.canSelfStorekeeperIssue(position, entitlement, request, hasOpenLossCase);

    item.requestId = this.getSelfStorekeeperRequestId(position, entitlement, request);
    item.hasActiveRequest = this.hasSelfStorekeeperActiveRequest(position, entitlement, request);
    item.canIssue = canIssue;
    item.issueDisabled = !canIssue;
    item.rejectDisabled = this.isSelfStorekeeperRejectDisabled(position, entitlement, request);
    item.rejectDisabledReasonKey = this.getSelfStorekeeperRejectDisabledReasonKey(
      position,
      entitlement,
      request
    );
    item.issueDisabledReasonKey = this.getSelfStorekeeperIssueDisabledReasonKey(
      position,
      entitlement,
      request,
      hasOpenLossCase
    );
    return item;
  }

  private createStorekeeperItem(
    position: EmployeeClothingPosition,
    entitlement: EmployeeClothingEntitlement,
    request: ClothingRequest,
    hasOpenLossCase: boolean
  ): ProfileClothingViewItem {
    var item = this.createItem(position, entitlement, request, hasOpenLossCase);

    item.requestId = this.getActiveRequestId(position, entitlement, request);
    item.hasActiveRequest = this.hasActiveRequest(position, entitlement, request);
    item.issueByRequest = this.shouldIssueByRequest(position, entitlement, request);
    item.canIssue = this.canStorekeeperIssue(position, entitlement, request, hasOpenLossCase);
    item.issueDisabled = !item.canIssue;
    item.issueDisabledReasonKey = this.getStorekeeperIssueDisabledReasonKey(
      position,
      entitlement,
      request,
      hasOpenLossCase
    );
    item.rejectDisabled = this.isStorekeeperRejectDisabled(position, entitlement, request);
    item.rejectDisabledReasonKey = this.getStorekeeperRejectDisabledReasonKey(
      position,
      entitlement,
      request
    );
    return item;
  }

  private canHeadExtend(position: EmployeeClothingPosition): boolean {
    if (!position || !position.positionId) {
      return false;
    }
    if (position.activeQuantity < 1) {
      return false;
    }
    if (position.hasActiveRequest === true) {
      return false;
    }
    return position.status !== POSITION_STATUS_RESET_AFTER_LOSS;
  }

  private canIssue(
    position: EmployeeClothingPosition,
    entitlement: EmployeeClothingEntitlement,
    request: ClothingRequest,
    hasOpenLossCase: boolean
  ): boolean {
    if (hasOpenLossCase) {
      return false;
    }
    if (this.hasTemporaryNoStockCoverage(position, entitlement)) {
      return false;
    }
    return this.canIssueAvailableQuantity(position, entitlement, request);
  }

  private canStorekeeperIssue(
    position: EmployeeClothingPosition,
    entitlement: EmployeeClothingEntitlement,
    request: ClothingRequest,
    hasOpenLossCase: boolean
  ): boolean {
    var source = this.getStorekeeperActionSource(position, entitlement);

    if (this.hasTemporaryNoStockCoverage(position, entitlement)) {
      return false;
    }
    if (!this.hasStorekeeperIssueFlags(source)) {
      return this.canIssue(position, entitlement, request, hasOpenLossCase);
    }
    if (this.canUseStorekeeperRequestIssue(position, entitlement, request)) {
      return true;
    }
    return this.canUseStorekeeperDirectIssue(position, entitlement);
  }

  private canSelfStorekeeperIssue(
    position: EmployeeClothingPosition,
    entitlement: EmployeeClothingEntitlement,
    request: ClothingRequest,
    hasOpenLossCase: boolean
  ): boolean {
    if (hasOpenLossCase) {
      return false;
    }
    if (this.hasTemporaryNoStockCoverage(position, entitlement)) {
      return false;
    }
    return this.canIssueAvailableQuantity(position, entitlement, request);
  }

  private canIssueAvailableQuantity(
    position: EmployeeClothingPosition,
    entitlement: EmployeeClothingEntitlement,
    request: ClothingRequest
  ): boolean {
    if (this.getAvailableIssueQuantity(position, entitlement, request) < 1) {
      return false;
    }
    if (this.getQuantity(position, entitlement) < 1) {
      return true;
    }
    return this.hasActiveRequest(position, entitlement, request);
  }

  private isIssueAllowed(
    position: EmployeeClothingPosition,
    entitlement: EmployeeClothingEntitlement
  ): boolean {
    if (position && typeof position.requestAllowed === 'boolean') {
      return position.requestAllowed;
    }
    return this.isEntitlementRequestAllowed(entitlement);
  }

  private isRequestDisabled(
    position: EmployeeClothingPosition,
    entitlement: EmployeeClothingEntitlement,
    request: ClothingRequest,
    hasOpenLossCase: boolean
  ): boolean {
    if (this.hasTemporaryNoStockCoverage(position, entitlement)) {
      return true;
    }
    if (!position) {
      return !this.isEntitlementRequestAllowed(entitlement) || hasOpenLossCase || !!request;
    }
    return !position.requestAllowed || hasOpenLossCase || !!request;
  }

  private isEntitlementRequestAllowed(entitlement: EmployeeClothingEntitlement): boolean {
    if (!entitlement) {
      return false;
    }
    if (typeof entitlement.requestAllowed === 'boolean') {
      return entitlement.requestAllowed;
    }
    return true;
  }

  private isLossDisabled(
    position: EmployeeClothingPosition,
    request: ClothingRequest,
    hasOpenLossCase: boolean
  ): boolean {
    if (!position) {
      return true;
    }
    if (this.isExpired(position, null)) {
      return true;
    }
    return hasOpenLossCase || position.activeQuantity < 1 || !!request;
  }

  private isRejectDisabled(
    position: EmployeeClothingPosition,
    entitlement: EmployeeClothingEntitlement,
    request: ClothingRequest
  ): boolean {
    if (!this.hasActiveRequest(position, entitlement, request)) {
      return true;
    }
    return !this.getActiveRequestId(position, entitlement, request).length;
  }

  private isStorekeeperRejectDisabled(
    position: EmployeeClothingPosition,
    entitlement: EmployeeClothingEntitlement,
    request: ClothingRequest
  ): boolean {
    var source = this.getStorekeeperActionSource(position, entitlement);

    if (!this.hasStorekeeperRejectFlag(source)) {
      return this.isRejectDisabled(position, entitlement, request);
    }
    if (source.rejectNoStockAllowed !== true) {
      return true;
    }
    return !this.getActiveRequestId(position, entitlement, request).length;
  }

  private getRequestDisabledReasonKey(
    position: EmployeeClothingPosition,
    entitlement: EmployeeClothingEntitlement,
    request: ClothingRequest,
    hasOpenLossCase: boolean
  ): string {
    if (request) {
      return CLOTHING_REASON_REQUEST_EXISTS;
    }
    if (hasOpenLossCase) {
      return CLOTHING_REASON_LOSS_PENDING;
    }
    if (this.hasTemporaryNoStockCoverage(position, entitlement)) {
      return CLOTHING_REASON_TEMPORARY_COVERAGE;
    }
    if (!this.isRequestDisabled(position, entitlement, request, hasOpenLossCase)) {
      return '';
    }
    return CLOTHING_REASON_REQUEST_NOT_ALLOWED;
  }

  private getLossDisabledReasonKey(
    position: EmployeeClothingPosition,
    request: ClothingRequest,
    hasOpenLossCase: boolean
  ): string {
    if (hasOpenLossCase) {
      return CLOTHING_REASON_LOSS_PENDING;
    }
    if (request) {
      return CLOTHING_REASON_REQUEST_IN_PROGRESS;
    }
    if (position && position.activeQuantity > 0) {
      return '';
    }
    return CLOTHING_REASON_NO_ISSUED_ITEM;
  }

  private getIssueDisabledReasonKey(
    position: EmployeeClothingPosition,
    entitlement: EmployeeClothingEntitlement,
    request: ClothingRequest,
    hasOpenLossCase: boolean
  ): string {
    if (this.canIssue(position, entitlement, request, hasOpenLossCase)) {
      return '';
    }
    if (hasOpenLossCase) {
      return CLOTHING_REASON_LOSS_PENDING;
    }
    if (request) {
      return CLOTHING_REASON_ISSUE_NOT_ALLOWED;
    }
    if (position && position.activeQuantity > 0) {
      return CLOTHING_REASON_ALREADY_ISSUED;
    }
    if (!this.isIssueAllowed(position, entitlement)) {
      return CLOTHING_REASON_ISSUE_NOT_ALLOWED;
    }
    return CLOTHING_REASON_NO_AVAILABLE_QUANTITY;
  }

  private getRejectDisabledReasonKey(
    position: EmployeeClothingPosition,
    entitlement: EmployeeClothingEntitlement,
    request: ClothingRequest
  ): string {
    if (!this.isRejectDisabled(position, entitlement, request)) {
      return '';
    }
    if (!this.hasActiveRequest(position, entitlement, request)) {
      return '';
    }
    return '';
  }

  private getSelfStorekeeperIssueDisabledReasonKey(
    position: EmployeeClothingPosition,
    entitlement: EmployeeClothingEntitlement,
    request: ClothingRequest,
    hasOpenLossCase: boolean
  ): string {
    if (this.canSelfStorekeeperIssue(position, entitlement, request, hasOpenLossCase)) {
      return '';
    }
    if (hasOpenLossCase) {
      return CLOTHING_REASON_LOSS_PENDING;
    }
    if (position && position.activeQuantity > 0) {
      return CLOTHING_REASON_ALREADY_ISSUED;
    }
    return CLOTHING_REASON_NO_AVAILABLE_QUANTITY;
  }

  private getStorekeeperIssueDisabledReasonKey(
    position: EmployeeClothingPosition,
    entitlement: EmployeeClothingEntitlement,
    request: ClothingRequest,
    hasOpenLossCase: boolean
  ): string {
    var source = this.getStorekeeperActionSource(position, entitlement);

    if (!this.hasStorekeeperIssueFlags(source)) {
      return this.getIssueDisabledReasonKey(position, entitlement, request, hasOpenLossCase);
    }
    if (this.canStorekeeperIssue(position, entitlement, request, hasOpenLossCase)) {
      return '';
    }
    return CLOTHING_REASON_ISSUE_NOT_ALLOWED;
  }

  private getStorekeeperRejectDisabledReasonKey(
    position: EmployeeClothingPosition,
    entitlement: EmployeeClothingEntitlement,
    request: ClothingRequest
  ): string {
    if (!this.isStorekeeperRejectDisabled(position, entitlement, request)) {
      return '';
    }
    return CLOTHING_REASON_REJECT_NOT_ALLOWED;
  }

  private getSelfStorekeeperRejectDisabledReasonKey(
    position: EmployeeClothingPosition,
    entitlement: EmployeeClothingEntitlement,
    request: ClothingRequest
  ): string {
    if (!this.isSelfStorekeeperRejectDisabled(position, entitlement, request)) {
      return '';
    }
    if (!this.hasSelfStorekeeperActiveRequest(position, entitlement, request)) {
      return '';
    }
    return '';
  }

  private calculateLifePercent(
    position: EmployeeClothingPosition,
    entitlement: EmployeeClothingEntitlement
  ): number {
    if (!position) {
      return 0;
    }
    var totalDays = this.getTotalWorkingDays(position, entitlement);
    var remainingDays = this.getRemainingWorkingDays(position, entitlement);

    if (!totalDays) {
      return 0;
    }
    return this.normalizePercent(Math.round((remainingDays / totalDays) * 100));
  }

  private createLifePercentText(lifePercent: number): string {
    return lifePercent + '%';
  }

  private hasTemporaryNoStockCoverage(
    position: EmployeeClothingPosition,
    entitlement: EmployeeClothingEntitlement
  ): boolean {
    if (position && position.temporarilyCoveredByNoStock === true) {
      return true;
    }
    if (entitlement && entitlement.temporarilyCoveredByNoStock === true) {
      return true;
    }
    return false;
  }

  private getTemporaryNoStockCoverageUntil(
    position: EmployeeClothingPosition,
    entitlement: EmployeeClothingEntitlement
  ): string {
    if (position && position.temporaryNoStockCoverageUntil) {
      return position.temporaryNoStockCoverageUntil;
    }
    if (entitlement && entitlement.temporaryNoStockCoverageUntil) {
      return entitlement.temporaryNoStockCoverageUntil;
    }
    return '';
  }

  private isTemporaryCoverageOnly(
    position: EmployeeClothingPosition,
    entitlement: EmployeeClothingEntitlement
  ): boolean {
    return this.hasTemporaryNoStockCoverage(position, entitlement) && !position;
  }

  private normalizePercent(value: number): number {
    if (value < 0) {
      return 0;
    }
    if (value > 100) {
      return 100;
    }
    return value;
  }

  private getTotalWorkingDays(
    position: EmployeeClothingPosition,
    entitlement: EmployeeClothingEntitlement
  ): number {
    if (position && typeof position.totalGrantedWorkingDays === 'number') {
      return position.totalGrantedWorkingDays;
    }
    if (entitlement && typeof entitlement.totalGrantedWorkingDays === 'number') {
      return entitlement.totalGrantedWorkingDays;
    }
    if (position && typeof position.serviceLifeWorkingDays === 'number') {
      return position.serviceLifeWorkingDays;
    }
    if (entitlement && typeof entitlement.serviceLifeWorkingDays === 'number') {
      return entitlement.serviceLifeWorkingDays;
    }
    return 0;
  }

  private getRemainingWorkingDays(
    position: EmployeeClothingPosition,
    entitlement: EmployeeClothingEntitlement
  ): number {
    if (position && typeof position.remainingWorkingDays === 'number') {
      return position.remainingWorkingDays;
    }
    if (entitlement && typeof entitlement.remainingWorkingDays === 'number') {
      return entitlement.remainingWorkingDays;
    }
    return this.getTotalWorkingDays(position, entitlement);
  }

  private createServiceLifeText(
    position: EmployeeClothingPosition,
    entitlement: EmployeeClothingEntitlement
  ): string {
    var remaining = this.getRemainingWorkingDays(position, entitlement);
    var total = this.getTotalWorkingDays(position, entitlement);
    var workingDays = this.translate.instant('CLOTHING_WORKING_DAYS');
    return remaining + ' / ' + total + ' ' + workingDays;
  }

  private getStatus(
    position: EmployeeClothingPosition,
    entitlement: EmployeeClothingEntitlement
  ): string {
    if (position && position.status) {
      return position.status;
    }
    if (entitlement && entitlement.status) {
      return entitlement.status;
    }
    return '';
  }

  private isExpired(
    position: EmployeeClothingPosition,
    entitlement: EmployeeClothingEntitlement
  ): boolean {
    if (position && position.expired === true) {
      return true;
    }
    if (entitlement && entitlement.expired === true) {
      return true;
    }
    return this.getStatus(position, entitlement) === 'EXPIRED';
  }

  private getIssueQuantity(
    position: EmployeeClothingPosition,
    entitlement: EmployeeClothingEntitlement,
    request: ClothingRequest
  ): number {
    return this.getDirectIssueQuantity(position, entitlement, request);
  }

  private hasActiveRequest(
    position: EmployeeClothingPosition,
    entitlement: EmployeeClothingEntitlement,
    request: ClothingRequest
  ): boolean {
    var source = this.getStorekeeperActionSource(position, entitlement);

    if (source && typeof source.hasActiveRequest === 'boolean') {
      return source.hasActiveRequest;
    }
    return !!request;
  }

  private hasSelfStorekeeperActiveRequest(
    position: EmployeeClothingPosition,
    entitlement: EmployeeClothingEntitlement,
    request: ClothingRequest
  ): boolean {
    var source = this.getSelfStorekeeperActionSource(position, entitlement);

    if (source && typeof source.hasActiveRequest === 'boolean') {
      return source.hasActiveRequest;
    }
    return !!request;
  }

  private getActiveRequestId(
    position: EmployeeClothingPosition,
    entitlement: EmployeeClothingEntitlement,
    request: ClothingRequest
  ): string {
    var source = this.getStorekeeperActionSource(position, entitlement);

    if (request && request.id) {
      return request.id;
    }
    if (source && source.activeRequestId) {
      return source.activeRequestId;
    }
    return '';
  }

  private getSelfStorekeeperRequestId(
    position: EmployeeClothingPosition,
    entitlement: EmployeeClothingEntitlement,
    request: ClothingRequest
  ): string {
    var source = this.getSelfStorekeeperActionSource(position, entitlement);

    if (request && request.id) {
      return request.id;
    }
    if (source && source.activeRequestId) {
      return source.activeRequestId;
    }
    return '';
  }

  private getStorekeeperActionSource(
    position: EmployeeClothingPosition,
    entitlement: EmployeeClothingEntitlement
  ): ClothingStorekeeperActionSource {
    if (position) {
      return position;
    }
    return entitlement;
  }

  private getSelfStorekeeperActionSource(
    position: EmployeeClothingPosition,
    entitlement: EmployeeClothingEntitlement
  ): ClothingStorekeeperActionSource {
    if (this.hasRequestFields(entitlement)) {
      return entitlement;
    }
    return this.getStorekeeperActionSource(position, entitlement);
  }

  private hasRequestFields(source: ClothingStorekeeperActionSource): boolean {
    if (!source) {
      return false;
    }
    if (typeof source.hasActiveRequest === 'boolean') {
      return true;
    }
    return !!source.activeRequestId;
  }

  private hasStorekeeperIssueFlags(source: ClothingStorekeeperActionSource): boolean {
    if (!source) {
      return false;
    }
    if (typeof source.directIssueAllowed === 'boolean') {
      return true;
    }
    return typeof source.issueByRequestAllowed === 'boolean';
  }

  private hasStorekeeperRejectFlag(source: ClothingStorekeeperActionSource): boolean {
    if (!source) {
      return false;
    }
    return typeof source.rejectNoStockAllowed === 'boolean';
  }

  private canUseStorekeeperRequestIssue(
    position: EmployeeClothingPosition,
    entitlement: EmployeeClothingEntitlement,
    request: ClothingRequest
  ): boolean {
    var source = this.getStorekeeperActionSource(position, entitlement);

    if (!source || source.issueByRequestAllowed !== true) {
      return false;
    }
    return this.getActiveRequestId(position, entitlement, request).length > 0;
  }

  private canUseStorekeeperDirectIssue(
    position: EmployeeClothingPosition,
    entitlement: EmployeeClothingEntitlement
  ): boolean {
    var source = this.getStorekeeperActionSource(position, entitlement);

    if (!source) {
      return false;
    }
    return source.directIssueAllowed === true;
  }

  private shouldIssueByRequest(
    position: EmployeeClothingPosition,
    entitlement: EmployeeClothingEntitlement,
    request: ClothingRequest
  ): boolean {
    if (this.canUseStorekeeperRequestIssue(position, entitlement, request)) {
      return true;
    }
    return false;
  }

  private isSelfStorekeeperRejectDisabled(
    position: EmployeeClothingPosition,
    entitlement: EmployeeClothingEntitlement,
    request: ClothingRequest
  ): boolean {
    if (!this.hasSelfStorekeeperActiveRequest(position, entitlement, request)) {
      return true;
    }
    return !this.getSelfStorekeeperRequestId(position, entitlement, request).length;
  }

  private getDirectIssueQuantity(
    position: EmployeeClothingPosition,
    entitlement: EmployeeClothingEntitlement,
    request: ClothingRequest
  ): number {
    var availableQuantity = this.getAvailableIssueQuantity(position, entitlement, request);

    if (availableQuantity > 0) {
      return availableQuantity;
    }
    return 1;
  }

  private getAvailableIssueQuantity(
    position: EmployeeClothingPosition,
    entitlement: EmployeeClothingEntitlement,
    request: ClothingRequest
  ): number {
    var currentQuantity = this.getQuantity(position, entitlement);
    var maxQuantity = this.getMaxQuantity(position, entitlement, request);

    if (maxQuantity <= currentQuantity) {
      return 0;
    }
    return maxQuantity - currentQuantity;
  }

  private getCatalogItemId(
    position: EmployeeClothingPosition,
    entitlement: EmployeeClothingEntitlement
  ): string {
    if (position) {
      return position.catalogItemId;
    }
    return entitlement.catalogItemId;
  }

  private getCatalogItemName(
    position: EmployeeClothingPosition,
    entitlement: EmployeeClothingEntitlement
  ): string {
    if (position) {
      return position.catalogItemName;
    }
    return entitlement.catalogItemName;
  }

  private getSeason(
    position: EmployeeClothingPosition,
    entitlement: EmployeeClothingEntitlement
  ): string {
    if (position) {
      return position.season;
    }
    return entitlement.season;
  }

  private getQuantity(
    position: EmployeeClothingPosition,
    entitlement: EmployeeClothingEntitlement
  ): number {
    if (position) {
      return position.activeQuantity;
    }
    return this.getEntitlementActiveQuantity(entitlement);
  }

  private getMaxQuantity(
    position: EmployeeClothingPosition,
    entitlement: EmployeeClothingEntitlement,
    request: ClothingRequest
  ): number {
    if (this.hasPositionMaxQuantity(position)) {
      return position.effectiveAllowedQuantity;
    }
    if (request && typeof request.effectiveAllowedQuantity === 'number') {
      return request.effectiveAllowedQuantity;
    }
    return this.getEntitlementMaxQuantity(entitlement);
  }

  private getHeadMaxQuantity(position: EmployeeClothingPosition): number {
    if (this.hasPositionMaxQuantity(position)) {
      return position.effectiveAllowedQuantity;
    }
    return position.activeQuantity;
  }

  private hasPositionMaxQuantity(position: EmployeeClothingPosition): boolean {
    if (!position) {
      return false;
    }
    return typeof position.effectiveAllowedQuantity === 'number';
  }

  private getEntitlementMaxQuantity(entitlement: EmployeeClothingEntitlement): number {
    if (!entitlement) {
      return 0;
    }
    if (typeof entitlement.effectiveAllowedQuantity === 'number') {
      return entitlement.effectiveAllowedQuantity;
    }
    if (typeof entitlement.quantity === 'number') {
      return entitlement.quantity;
    }
    if (typeof entitlement.entitledQuantity === 'number') {
      return entitlement.entitledQuantity;
    }
    if (typeof entitlement.allowedQuantity === 'number') {
      return entitlement.allowedQuantity;
    }
    return 0;
  }

  private getEntitlementActiveQuantity(entitlement: EmployeeClothingEntitlement): number {
    if (!entitlement) {
      return 0;
    }
    if (typeof entitlement.activeQuantity === 'number') {
      return entitlement.activeQuantity;
    }
    if (this.hasLegacyEntitlementQuantity(entitlement)) {
      return entitlement.quantity;
    }
    return 0;
  }

  private hasLegacyEntitlementQuantity(entitlement: EmployeeClothingEntitlement): boolean {
    if (typeof entitlement.quantity !== 'number') {
      return false;
    }
    if (typeof entitlement.effectiveAllowedQuantity === 'number') {
      return false;
    }
    if (typeof entitlement.entitledQuantity === 'number') {
      return false;
    }
    return typeof entitlement.allowedQuantity !== 'number';
  }

  private getIssueDate(position: EmployeeClothingPosition): string {
    if (!position) {
      return '';
    }
    return position.lastIssueDate;
  }

  private createSeasonKey(season: string): string {
    return 'CLOTHING_SEASON_' + season;
  }

  private createLifeBarClass(
    position: EmployeeClothingPosition,
    entitlement: EmployeeClothingEntitlement,
    lifePercent: number,
    hasIssuedPosition: boolean
  ): string {
    if (!hasIssuedPosition) {
      return 'employee-clothing__life-bar employee-clothing__life-bar--planned';
    }
    if (this.isExpired(position, entitlement)) {
      return 'employee-clothing__life-bar employee-clothing__life-bar--danger';
    }
    if (lifePercent < 1) {
      return 'employee-clothing__life-bar employee-clothing__life-bar--danger';
    }
    if (lifePercent < 25) {
      return 'employee-clothing__life-bar employee-clothing__life-bar--warning';
    }
    return 'employee-clothing__life-bar';
  }
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/services/profile-clothing-view.service.ts